import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { loadExercisesWithRelations, type AdminExerciseVM } from "@/integrations/backend/exercises";
import { MOVEMENT_PATTERNS } from "@/integrations/backend/enums";
import { Filter, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ExerciseCard from "@/components/admin/exercises/ExerciseCard";
import GroupedMuscleActionFilterItems from "@/components/admin/exercises/GroupedMuscleActionFilterItems";
import {
  EXERCISE_DIFFICULTY_OPTIONS,
  matchesExerciseLibraryFilters,
} from "@/lib/exercise-library-filters";
import { exerciseTypeCatalog } from "@/lib/exercise-types";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onAddMany: (ids: string[]) => void | Promise<void>;
};

function uniqSorted(items: string[]): string[] {
  return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b));
}

function filterButtonClass(isActive: boolean): string {
  return isActive
    ? "border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
    : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50";
}

export default function DBExercisePickerDialog({ open, onOpenChange, onAddMany }: Props) {
  const [query, setQuery] = useState("");
  const [movementFilter, setMovementFilter] = useState<string[]>([]);
  const [equipmentFilter, setEquipmentFilter] = useState<string[]>([]);
  const [primaryMuscleFilter, setPrimaryMuscleFilter] = useState<string[]>([]);
  const [muscleActionFilter, setMuscleActionFilter] = useState<string[]>([]);
  const [difficultyFilter, setDifficultyFilter] = useState<string[]>([]);
  const [exerciseTypeFilter, setExerciseTypeFilter] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [exercises, setExercises] = useState<AdminExerciseVM[]>([]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const rows = await loadExercisesWithRelations();
        if (!active) return;
        setExercises(rows.filter((r) => !r.is_deleted));
      } catch (e: any) {
        toast.error("Could not load exercises", { description: e?.message ?? String(e) });
        setExercises([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open]);

  const equipmentOptions = useMemo(
    () => uniqSorted(exercises.flatMap((e) => e.equipment || [])),
    [exercises]
  );
  const primaryMuscleOptions = useMemo(
    () => uniqSorted(exercises.flatMap((e) => e.primary_muscles || []).map(String)),
    [exercises]
  );
  const availableExerciseTypeOptions = useMemo(() => {
    const ids = uniqSorted(exercises.flatMap((e) => (e.exercise_types || []).map(String)));
    return exerciseTypeCatalog
      .filter((t) => ids.includes(t.id))
      .map((t) => ({ id: t.id, label: t.label }));
  }, [exercises]);

  const filtered = useMemo(() => {
    return exercises.filter((e) =>
      matchesExerciseLibraryFilters(e, {
        searchQuery: query,
        movementPatterns: movementFilter,
        muscleGroups: primaryMuscleFilter,
        equipment: equipmentFilter,
        muscleActions: muscleActionFilter,
        difficulties: difficultyFilter,
        exerciseTypes: exerciseTypeFilter,
      })
    );
  }, [
    equipmentFilter,
    exerciseTypeFilter,
    exercises,
    movementFilter,
    muscleActionFilter,
    primaryMuscleFilter,
    difficultyFilter,
    query,
  ]);

  const activeFilterCount =
    movementFilter.length +
    equipmentFilter.length +
    primaryMuscleFilter.length +
    muscleActionFilter.length +
    difficultyFilter.length +
    exerciseTypeFilter.length;

  const clearAll = () => {
    setQuery("");
    setMovementFilter([]);
    setEquipmentFilter([]);
    setPrimaryMuscleFilter([]);
    setMuscleActionFilter([]);
    setDifficultyFilter([]);
    setExerciseTypeFilter([]);
    setSelected({});
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) clearAll();
      }}
    >
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-5xl h-[min(760px,calc(100vh-1.5rem))] max-h-[calc(100vh-1.5rem)] overflow-hidden bg-white text-gray-900 flex flex-col">
        <DialogHeader>
          <DialogTitle>Browse exercises</DialogTitle>
          <DialogDescription>Filter, select multiple, then add to the workout.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 flex-1 min-h-0">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search by name, primary muscle, equipment"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 min-w-[240px] bg-white text-gray-900 border-gray-300"
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(movementFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Movement
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Movement pattern</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(MOVEMENT_PATTERNS as unknown as string[]).map((pattern) => (
                  <DropdownMenuCheckboxItem
                    key={pattern}
                    checked={movementFilter.includes(pattern)}
                    onCheckedChange={(v) =>
                      setMovementFilter((prev) => (v ? [...prev, pattern] : prev.filter((x) => x !== pattern)))
                    }
                  >
                    {pattern}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(primaryMuscleFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Primary muscles
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64">
                <DropdownMenuLabel>Primary muscles</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {primaryMuscleOptions.map((mg) => (
                  <DropdownMenuCheckboxItem
                    key={mg}
                    checked={primaryMuscleFilter.includes(mg)}
                    onCheckedChange={(v) => setPrimaryMuscleFilter((prev) => (v ? [...prev, mg] : prev.filter((x) => x !== mg)))}
                  >
                    {mg}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(equipmentFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Equipment
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64">
                <DropdownMenuLabel>Equipment</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {equipmentOptions.map((eq) => (
                  <DropdownMenuCheckboxItem
                    key={eq}
                    checked={equipmentFilter.includes(eq)}
                    onCheckedChange={(v) => setEquipmentFilter((prev) => (v ? [...prev, eq] : prev.filter((x) => x !== eq)))}
                  >
                    {eq}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(muscleActionFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Muscle actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[min(100vw-2rem,28rem)] max-h-[400px] overflow-y-auto">
                <DropdownMenuLabel>Muscle actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <GroupedMuscleActionFilterItems
                  selected={muscleActionFilter}
                  onToggle={(action, checked) => {
                    setMuscleActionFilter((prev) =>
                      checked ? [...prev, action] : prev.filter((x) => x !== action)
                    );
                  }}
                  onClear={() => setMuscleActionFilter([])}
                />
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(difficultyFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Difficulty
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Difficulty</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {EXERCISE_DIFFICULTY_OPTIONS.map((d) => (
                  <DropdownMenuCheckboxItem
                    key={d}
                    checked={difficultyFilter.includes(d)}
                    onCheckedChange={(v) => setDifficultyFilter((prev) => (v ? [...prev, d] : prev.filter((x) => x !== d)))}
                  >
                    {d}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(exerciseTypeFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Types
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 max-h-[400px] overflow-y-auto">
                <DropdownMenuLabel>Exercise types</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {availableExerciseTypeOptions.map((type) => (
                  <DropdownMenuCheckboxItem
                    key={type.id}
                    checked={exerciseTypeFilter.includes(type.id)}
                    onCheckedChange={(v) =>
                      setExerciseTypeFilter((prev) => (v ? [...prev, type.id] : prev.filter((x) => x !== type.id)))
                    }
                  >
                    {type.label}
                  </DropdownMenuCheckboxItem>
                ))}
                {availableExerciseTypeOptions.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">No exercise types in catalog</div>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>

            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  setMovementFilter([]);
                  setEquipmentFilter([]);
                  setPrimaryMuscleFilter([]);
                  setMuscleActionFilter([]);
                  setDifficultyFilter([]);
                  setExerciseTypeFilter([]);
                }}
              >
                Clear filters
              </Button>
            )}
          </div>

          <div className="border rounded-md overflow-hidden flex-1 min-h-0">
            <ScrollArea className="h-full">
              <div className="p-3">
                {loading ? (
                  <div className="py-10 text-center text-sm text-gray-600">Loading exercises…</div>
                ) : null}

                {!loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((ex) => {
                      const isSelected = Boolean(selected[ex.id]);
                      const toggle = () => setSelected((prev) => ({ ...prev, [ex.id]: !Boolean(prev[ex.id]) }));
                      return (
                        <ExerciseCard
                          key={ex.id}
                          exercise={ex}
                          selected={isSelected}
                          onClick={toggle}
                          topRight={
                            <div
                              className="bg-white/90 rounded-full p-2 shadow-sm"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                aria-label={isSelected ? `Deselect ${ex.name}` : `Select ${ex.name}`}
                                checked={isSelected}
                                onCheckedChange={() => toggle()}
                              />
                            </div>
                          }
                        />
                      );
                    })}
                  </div>
                ) : null}

                {!loading && filtered.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-600">No exercises match your filters.</div>
                ) : null}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              clearAll();
            }}
            className="text-black border-gray-300 hover:bg-gray-100"
          >
            Cancel
          </Button>
          <Button
            disabled={selectedIds.length === 0 || adding}
            onClick={async () => {
              setAdding(true);
              try {
                await onAddMany(selectedIds);
                onOpenChange(false);
                clearAll();
              } finally {
                setAdding(false);
              }
            }}
          >
            {adding ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Adding…
              </>
            ) : (
              <>Add selected{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


